<script setup>
const api = useApi()
const { t } = useI18n()
const { currentCustomerId, hasFamily, familyId } = useSessionData()

const hasFavoritedItems = ref(false)
const pageSize = 15
const params = useUrlSearchParams()

async function apiRequest(paging) {
  return await api.listContentCollections({
    queryParameters: {
      page: paging.currentPage,
      page_size: paging.pageSize,
    },
    expand: [
      ...(currentCustomerId ? ['active_customer_has_access'] : []),
      'content_thumbnail',
    ],
  })
}

async function checkForFavoritedItems() {
  if (!currentCustomerId)
    return

  const favoritesCount = (
    await api.listContent({
      queryParameters: {
        favorited_by: currentCustomerId,
        page_size: 1,
        fields: '',
      },
    })
  )?.count

  hasFavoritedItems.value = favoritesCount > 0
}

onMounted(() => {
  checkForFavoritedItems()
})
</script>

<template>
  <div class="mx-4 flex flex-col gap-4 border-1 rounded bg-white p-6">
    <header class="flex items-center justify-between gap-4 lt-md:flex-col">
      <h1 class="h3 text-secondary">
        {{ t('All Collections') }}
      </h1>
      <BaseLink
        v-if="hasFavoritedItems"
        url="/on_demand/favorites"
        class="flex items-center gap-2 border-1 rounded text-text-body p-block-3 p-inline-4 hover:shadow-sm"
      >
        <DSIcon
          icon="i-far-heart"
          size="32"
          class="text-favorites"
        />
        <span>{{ t('My Favorites') }}</span>
      </BaseLink>
    </header>

    <PaginatedDataTemplate
      :page-size="pageSize"
      :api-request="apiRequest"
      :initial-page="params.page || 1"
    >
      <template #noDataAlert>
        <DSAlert
          type="info"
          :no-close="true"
          class="rounded-1.5 p-4"
        >
          <p class="text-sm text-text-500 font-700">
            {{ t('There’s no content to display') }}.
          </p>
        </DSAlert>
      </template>
      <template #data="{ entries }">
        <div class="grid-container">
          <template
            v-for="collection in entries"
            :key="collection.id"
          >
            <OnDemandCollectionCard :collection="collection" />
          </template>
        </div>
      </template>
    </PaginatedDataTemplate>
  </div>
</template>

<style scoped lang="scss">
.grid-container {
  @apply grid grid-cols-3 lt-lg:grid-cols-2 lt-md:grid-cols-1 gap-4;
}
</style>

<i18n>
{
  "en-us": {
    "All Collections": "All Collections",
    "My Favorites": "My Favorites",
    "There’s no content to display": "There’s no content to display"
  }
}
</i18n>

<i18n locale="de" lang="json">
{
  "All Collections": "Alle Kollektionen",
  "My Favorites": "Meine Favoriten",
  "There’s no content to display": "Keine Inhalte zum Anzeigen"
}
</i18n>

<i18n locale="es" lang="json">
{
  "All Collections": "Todas las colecciones",
  "My Favorites": "Mis favoritos",
  "There’s no content to display": "No hay contenido que mostrar"
}
</i18n>

<i18n locale="fr" lang="json">
{
  "All Collections": "Toutes les collections",
  "My Favorites": "Mes favoris",
  "There’s no content to display": "Aucun contenu à afficher"
}
</i18n>
